<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Animierter Background -->
    <div class="fixed inset-0 -z-10">
      <!-- Gradient Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-100 dark:from-gray-900 dark:via-blue-950/20 dark:to-gray-800"></div>
      
      <!-- Animierte Gradient Blobs -->
      <div class="absolute top-0 -left-4 w-96 h-96 bg-blue-400/20 dark:bg-blue-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob"></div>
      <div class="absolute top-0 -right-4 w-96 h-96 bg-purple-400/20 dark:bg-purple-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      <div class="absolute -bottom-8 left-20 w-96 h-96 bg-pink-400/20 dark:bg-pink-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
      
      <!-- Grid Pattern Overlay -->
      <div class="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)]"></div>
    </div>
    
    <Navigation />
    
    <!-- Main Content -->
    <section class="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-32 md:pt-40 pb-20">
      <div 
        ref="contentElement"
        class="transition-all duration-700 ease-out"
        :class="contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
      >
        <!-- Header -->
        <div class="text-center mb-12">
          <h1 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Datenschutzerklärung
          </h1>
          <div class="mt-4 h-0.5 w-16 bg-blue-600 dark:bg-blue-400 mx-auto"></div>
        </div>

        <!-- Content Card -->
        <div class="bg-white dark:bg-gray-800 p-8 md:p-10 rounded-xl shadow-lg dark:shadow-gray-900/50 border border-gray-200 dark:border-gray-700">
          <div class="prose prose-gray dark:prose-invert max-w-none">
            <!-- Einleitung -->
            <section class="mb-8">
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
              </p>
            </section>

            <!-- Verantwortlicher -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                1. Verantwortlicher für die Datenverarbeitung
              </h2>
              <div class="space-y-2 text-gray-700 dark:text-gray-300">
                <p><strong>Verantwortlich im Sinne der DSGVO:</strong></p>
                <p>
                  Maximilian Reitsberger<br>
                  Marktplatz 5<br>
                  85598 Baldham<br>
                  Deutschland
                </p>
                <p class="mt-4">
                  <strong>Kontakt:</strong><br>
                  E-Mail: 
                  <a 
                    :href="`mailto:${contactEmail}`" 
                    class="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 transition-colors"
                  >
                    {{ contactEmail }}
                  </a>
                </p>
              </div>
            </section>

            <!-- Hosting -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                2. Hosting und Backend
              </h2>
              <div class="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  Wir hosten die Inhalte unserer Website bei folgendem Anbieter:
                </p>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Google Firebase</h3>
                <p>
                  Anbieter ist die Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland (nachfolgend „Google“).
                </p>
                <p>
                  <strong>Hosting:</strong><br>
                  Die statischen Inhalte dieser Website werden über Google Firebase Hosting ausgeliefert. Dabei werden Ihre IP-Adresse und weitere technische Daten (siehe „Server-Log-Dateien“) kurzzeitig verarbeitet, um die Inhalte an Ihren Browser zu übermitteln. Die Serverstandorte befinden sich vorwiegend in Europa.
                </p>
                <p>
                  <strong>Datenbank (Firestore):</strong><br>
                  Daten, die Sie über das Kontaktformular senden, werden in der Google Cloud Firestore Datenbank gespeichert.
                </p>
                <p>
                  <strong>Rechtsgrundlage:</strong><br>
                  Die Nutzung von Google Firebase erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes Interesse an einer möglichst sicheren, schnellen und effizienten Bereitstellung seines Online-Angebots.
                </p>
                <p>
                  Weitere Informationen finden Sie in der Datenschutzerklärung von Google: 
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 hover:underline">https://policies.google.com/privacy</a>.
                </p>
              </div>
            </section>

            <!-- Kontaktformular -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                3. Datenerfassung auf dieser Website
              </h2>
              
              <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-3">
                3.1 Kontaktformular
              </h3>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Die Speicherung erfolgt in unserer Datenbank bei Google Firebase (Firestore). Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
              </p>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Rechtsgrundlage:</strong> Die Verarbeitung der in das Kontaktformular eingegebenen Daten erfolgt ausschließlich auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Sie können diese Einwilligung jederzeit widerrufen. Dazu reicht eine formlose Mitteilung per E-Mail an uns. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitungsvorgänge bleibt vom Widerruf unberührt.
              </p>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Speicherdauer:</strong> Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung entfällt (z. B. nach abgeschlossener Bearbeitung Ihrer Anfrage). Zwingende gesetzliche Bestimmungen – insbesondere Aufbewahrungsfristen – bleiben unberührt.
              </p>
            </section>

            <!-- Server-Log-Dateien -->
            <section class="mb-8">
              <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-3">
                3.2 Server-Log-Dateien
              </h3>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:
              </p>
              <ul class="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-1">
                <li>Browsertyp und Browserversion</li>
                <li>verwendetes Betriebssystem</li>
                <li>Referrer URL</li>
                <li>Hostname des zugreifenden Rechners</li>
                <li>Uhrzeit der Serveranfrage</li>
                <li>IP-Adresse</li>
              </ul>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Rechtsgrundlage:</strong> Diese Daten werden nicht mit anderen Datenquellen zusammengeführt. Die Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes Interesse an der technisch fehlerfreien Darstellung und der Optimierung seiner Website – hierzu müssen die Server-Log-Files erfasst werden.
              </p>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Speicherdauer:</strong> Die Server-Log-Dateien werden automatisch nach einer bestimmten Zeit gelöscht, sobald sie zur Erfüllung des Zwecks nicht mehr erforderlich sind.
              </p>
            </section>

            <!-- Cookies -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                4. Cookies
              </h2>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Diese Website verwendet teilweise so genannte Cookies. Cookies richten auf Ihrem Rechner keinen Schaden an und enthalten keine Viren. Cookies dienen dazu, unser Angebot nutzerfreundlicher, effektiver und sicherer zu machen. Cookies sind kleine Textdateien, die auf Ihrem Rechner abgelegt werden und die Ihr Browser speichert.
              </p>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Die meisten der von uns verwendeten Cookies sind so genannte "Session-Cookies". Sie werden nach Ende Ihres Besuchs automatisch gelöscht. Andere Cookies bleiben auf Ihrem Endgerät gespeichert, bis Sie diese löschen. Diese Cookies ermöglichen es uns, Ihren Browser beim nächsten Besuch wiederzuerkennen.
              </p>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden und Cookies nur im Einzelfall erlauben, die Annahme von Cookies für bestimmte Fälle oder generell ausschließen sowie das automatische Löschen der Cookies beim Schließen des Browsers aktivieren. Bei der Deaktivierung von Cookies kann die Funktionalität dieser Website eingeschränkt sein.
              </p>
            </section>

            <!-- Ihre Rechte -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                5. Ihre Rechte
              </h2>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Sie haben jederzeit das Recht, Auskunft über Ihre bei uns gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger und den Zweck der Datenverarbeitung sowie ein Recht auf Berichtigung, Sperrung oder Löschung dieser Daten zu erhalten. Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit unter der im Impressum angegebenen Adresse an uns wenden.
              </p>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Sie haben insbesondere folgende Rechte:</strong>
              </p>
              <ul class="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-1">
                <li><strong>Auskunftsrecht</strong> (Art. 15 DSGVO): Sie können Auskunft über Ihre von uns verarbeiteten personenbezogenen Daten verlangen.</li>
                <li><strong>Berichtigungsrecht</strong> (Art. 16 DSGVO): Sie können die Berichtigung unrichtiger oder die Vervollständigung Ihrer bei uns gespeicherten personenbezogenen Daten verlangen.</li>
                <li><strong>Löschungsrecht</strong> (Art. 17 DSGVO): Sie können die Löschung Ihrer personenbezogenen Daten verlangen, soweit keine gesetzlichen Aufbewahrungspflichten bestehen.</li>
                <li><strong>Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO): Sie können die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten verlangen.</li>
                <li><strong>Widerspruchsrecht</strong> (Art. 21 DSGVO): Sie können der Verarbeitung Ihrer personenbezogenen Daten widersprechen.</li>
                <li><strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO): Sie können verlangen, dass wir Ihnen Ihre Daten in einem strukturierten, gängigen und maschinenlesbaren Format aushändigen.</li>
                <li><strong>Widerruf einer Einwilligung</strong> (Art. 7 Abs. 3 DSGVO): Sie haben das Recht, eine erteilte Einwilligung jederzeit zu widerrufen.</li>
              </ul>
            </section>

            <!-- Widerspruch -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                6. Widerspruch gegen Datenerfassung
              </h2>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Sie können der Erhebung und Verarbeitung Ihrer personenbezogenen Daten jederzeit widersprechen. Bitte kontaktieren Sie uns hierfür unter der im Impressum angegebenen E-Mail-Adresse.
              </p>
            </section>

            <!-- Änderungen -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                7. Änderung dieser Datenschutzerklärung
              </h2>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen. Für Ihren erneuten Besuch gilt dann die neue Datenschutzerklärung.
              </p>
            </section>

            <!-- Stand -->
            <section class="mb-8">
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-8">
                Stand: {{ new Date().toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' }) }}
              </p>
            </section>
          </div>
        </div>

        <!-- Back Link -->
        <div class="mt-8 text-center">
          <router-link 
            to="/contact" 
            class="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 transition-colors font-medium"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Zurück zur Kontaktseite
          </router-link>
        </div>
      </div>
    </section>

    <StandardFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import Navigation from '../components/Navigation.vue'
import StandardFooter from '../components/StandardFooter.vue'
import { useScrollAnimation } from '../composables/useScrollAnimation'
import { getContactInfo, getDefaultContactInfo } from '../firebase/contact'
import { logger } from '../utils/logger'

const contactEmail = ref('info@reitsberger-it.de')

// Scroll-Animationen
const { isVisible: contentVisible, targetElement: contentElement } = useScrollAnimation({
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px'
})

onMounted(async () => {
  try {
    const data = await getContactInfo()
    if (data?.email) {
      contactEmail.value = data.email
    } else {
      const defaultData = getDefaultContactInfo()
      if (defaultData.email && defaultData.email !== 'kontakt@example.com') {
        contactEmail.value = defaultData.email
      }
    }
  } catch (error) {
    logger.error('Fehler beim Laden der Kontaktdaten', error)
  }
})
</script>

<style scoped>
@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}
.animate-blob {
  animation: blob 7s infinite;
}
.animation-delay-2000 {
  animation-delay: 2s;
}
.animation-delay-4000 {
  animation-delay: 4s;
}
</style>

